import React, { useState } from "react";
import {
  AdminAccessRequest,
  createPostEventsAccessRequest,
} from "../../api/admin";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

interface Props {
  requests: AdminAccessRequest[];
  onRequestSubmitted: () => void;
  compact?: boolean;
  /** When true, user already has permission — do not show the request button. */
  granted?: boolean;
}

export default function RequestPostEventsAccess({
  requests,
  onRequestSubmitted,
  compact = false,
  granted = false,
}: Props) {
  const hasPending = requests.some((r) => r.status === "pending");
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createPostEventsAccessRequest(message || undefined);
      setShowModal(false);
      setMessage("");
      onRequestSubmitted();
    } catch (err) {
      setError(
        getUserFacingErrorMessage(err, "Could not submit request."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (granted) {
    return null;
  }

  return (
    <>
      {!hasPending && (
        <button
          type="button"
          className={compact ? "eh-btn-secondary text-sm" : "eh-btn-secondary"}
          onClick={() => {
            setShowModal(true);
            setError(null);
          }}
        >
          Request post events permission
        </button>
      )}

      {requests.length > 0 && (
        <div className={`space-y-3 ${compact ? "mt-4" : "mt-6"}`}>
          {requests.map((req) => (
            <div
              key={req.id}
              className="border border-slate-200 p-4 rounded-sm bg-slate-50 text-sm"
            >
              <p className="font-medium text-slate-900">Post events permission</p>
              <p className="text-slate-600 mt-1">
                Status:{" "}
                <span
                  className={
                    req.status === "approved"
                      ? "text-green-700 font-medium"
                      : req.status === "rejected"
                        ? "text-red-700 font-medium"
                        : "text-amber-700 font-medium"
                  }
                >
                  {req.status}
                </span>
                {" · "}
                Submitted {req.createdAt.slice(0, 10)}
              </p>
              {req.message && (
                <p className="mt-2 text-slate-700">{req.message}</p>
              )}
              {req.reviewNote && (
                <p className="mt-1 text-slate-500">Note: {req.reviewNote}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-sm max-w-md w-full shadow-lg">
            <h3 className="font-bold text-lg mb-2">
              Request post events permission
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              If approved, you can create new events. Each event you create will
              grant you full management access automatically.
            </p>
            {error && <ErrorAlert className="mb-3">{error}</ErrorAlert>}
            <label className="block text-sm mb-1">Message (optional)</label>
            <textarea
              className="w-full border border-slate-300 p-2 mb-4 text-sm"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Why do you need to post events?"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="eh-btn-secondary text-sm"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="eh-btn-primary text-sm"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
